/** Riders management page — account-level rider CRUD with modal for add/edit. */

import { useEffect, useState, useRef } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import type { Rider } from "@/types";

export function RidersPage() {
  const [riders, setRiders] = useState<Rider[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalRider, setModalRider] = useState<Rider | "new" | null>(null);

  const load = async () => {
    const r = await api.get<Rider[]>("/api/riders");
    setRiders(r);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const deleteRider = async (id: string) => {
    await api.delete(`/api/riders/${id}`);
    load();
  };

  const uploadPhoto = async (riderId: string, file: File) => {
    await api.upload(`/api/riders/${riderId}/photo`, file);
    load();
  };

  if (loading) return <div className="loading-screen"><div className="loading-spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Riders</h1>
          <p className="page-subtitle">Your rider roster — reusable across all competitions</p>
        </div>
        <Button onClick={() => setModalRider("new")}>Add Rider</Button>
      </div>

      {riders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No riders yet</div>
          <div className="empty-state-desc">Add riders to your account to use them in competitions.</div>
        </div>
      ) : (
        <div className="grid-3">
          {riders.map((rider) => (
            <div key={rider.id} className="card">
              <div className="flex items-center gap-3" style={{ marginBottom: 12 }}>
                <PhotoUpload
                  riderId={rider.id}
                  hasPhoto={rider.has_photo}
                  name={rider.name}
                  onUpload={(file) => uploadPhoto(rider.id, file)}
                />
                <div>
                  <div style={{ fontWeight: 700 }}>{rider.name}</div>
                  {rider.nickname && <div className="text-sm text-muted">&ldquo;{rider.nickname}&rdquo;</div>}
                  {rider.level && <div className="text-sm text-muted">{rider.level}</div>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setModalRider(rider)}>Edit</Button>
                <Button variant="danger" size="sm" onClick={() => deleteRider(rider.id)}>Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Rider Modal */}
      {modalRider !== null && (
        <RiderModal
          rider={modalRider === "new" ? null : modalRider}
          onClose={() => setModalRider(null)}
          onSaved={() => { setModalRider(null); load(); }}
          onUploadPhoto={uploadPhoto}
        />
      )}
    </div>
  );
}

/* ── Add / Edit Rider Modal ── */

function RiderModal({ rider, onClose, onSaved, onUploadPhoto }: {
  rider: Rider | null;
  onClose: () => void;
  onSaved: () => void;
  onUploadPhoto: (riderId: string, file: File) => Promise<void>;
}) {
  const isNew = rider === null;
  const [form, setForm] = useState({
    name: rider?.name ?? "",
    nickname: rider?.nickname ?? "",
    level: rider?.level ?? "",
    fun_fact: rider?.fun_fact ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [newRiderId, setNewRiderId] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (isNew) {
        const created = await api.post<Rider>("/api/riders", {
          name: form.name.trim(),
          nickname: form.nickname.trim() || null,
          level: form.level.trim() || null,
          fun_fact: form.fun_fact.trim() || null,
        });
        setNewRiderId(created.id);
      } else {
        await api.put(`/api/riders/${rider.id}`, {
          name: form.name.trim(),
          nickname: form.nickname.trim() || null,
          level: form.level.trim() || null,
          fun_fact: form.fun_fact.trim() || null,
        });
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = async (file: File) => {
    const id = rider?.id ?? newRiderId;
    if (!id) return;
    await onUploadPhoto(id, file);
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        display: "flex", justifyContent: "center", alignItems: "center",
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "var(--c-surface)", border: "1px solid var(--c-border)",
        padding: 32, width: "100%", maxWidth: 480,
      }}>
        {/* Avatar + name header */}
        <div className="flex items-center gap-3" style={{ marginBottom: 24 }}>
          <div
            style={{
              width: 52, height: 52, borderRadius: "50%",
              background: "var(--c-surface-3)", display: "flex",
              alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
              overflow: "hidden",
            }}
            onClick={() => photoInputRef.current?.click()}
          >
            {rider?.has_photo ? (
              <img
                src={`${import.meta.env.VITE_API_URL ?? ""}/api/riders/${rider.id}/photo`}
                alt={form.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 20 }}>
                {form.name.trim() ? form.name.trim().charAt(0).toUpperCase() : "?"}
              </span>
            )}
          </div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handlePhotoSelect(file);
            }}
          />
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {form.name.trim() || (isNew ? "New Rider" : "Rider")}
          </div>
        </div>

        <div className="flex-col gap-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            autoFocus
          />
          <Input
            label="Nickname"
            value={form.nickname}
            onChange={(e) => setForm({ ...form, nickname: e.target.value })}
            placeholder='e.g. "The King"'
          />
          <Input
            label="Level"
            value={form.level}
            onChange={(e) => setForm({ ...form, level: e.target.value })}
            placeholder="e.g. Pro, Juniors, Open"
          />
          <Input
            label="Fun Fact"
            value={form.fun_fact}
            onChange={(e) => setForm({ ...form, fun_fact: e.target.value })}
            placeholder="Something interesting about the rider"
          />
        </div>

        <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
          <Button onClick={save} loading={saving}>Save</Button>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

/* ── Photo Upload (inline on card) ── */

function PhotoUpload({ riderId, hasPhoto, name, onUpload }: {
  riderId: string;
  hasPhoto: boolean;
  name: string;
  onUpload: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      style={{
        width: 48, height: 48, borderRadius: "50%", overflow: "hidden",
        cursor: "pointer", border: "2px solid var(--c-border)", flexShrink: 0,
      }}
      onClick={() => inputRef.current?.click()}
    >
      {hasPhoto ? (
        <img
          src={`${import.meta.env.VITE_API_URL ?? ""}/api/riders/${riderId}/photo`}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div style={{
          width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
          background: "var(--c-surface-3)", color: "#fff", fontWeight: 700, fontSize: 18,
        }}>
          {name.charAt(0)}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onUpload(file);
        }}
      />
    </div>
  );
}
